import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Icon } from '@iq/react-components';
import { SignalViewer } from '@iq/comtrade-viewer';
import '@iq/comtrade-viewer/dist/index.css';

import { updateTfrTemplate, fetchTfrTemplates } from '../../bundles/tfr-templates';
import NewTemplateModal from './components/NewTemplateModal';
import Loader from '../Loader';
import { fetchExtendedSeriesData } from '../../bundles/variables';
import { getSiteVariables, getSources } from '../../bundles/sources';
import services from '../../services';
import { checkIsOnline, displayNotification, getNotification } from '../../bundles/notifications';
import SelectPollingDateRange from '../SelectPollingDateRange';
import {
  SIGNAL_VIEWER_DATETIME,
  DEFAULT_AGGREGATION_OPTIONS,
  DEFAULT_ON_PREMISE_AGGREGATION_OPTIONS,
  EVENT_TYPE_MACH,
} from '../../constants';
import ConfirmationDialog from '../ConfirmationDialog';
import {
  useDateSubscription,
  setPollingDateRange,
  getRangeFromLabel,
  getRangeFromPartial,
} from '../../bundles/application';
import { exportTimeSeriesToFile, useBackgroundFreeze } from '../../utils';
import { getBestFitGranularity } from '../../datetimeUtils';
import { useEventModal } from '../EventModal';
import { getUserEventTypes } from '../../bundles/events';
import config from '../../config';
import { getStarPlotVariables } from '../Visualization/visualizations/timeSeries/StarVisualization/helpers';

const PLOTTABLE_DATA_TYPES = new Set(['number', 'boolean']);
const isPlottableSignal = (variable) => PLOTTABLE_DATA_TYPES.has(variable.dataType);

// Since the duval and star point don't display all signals, we need to extract
// the signals that are actually assigned to plots
function getVisualizationSignalVariables(visualization) {
  switch (visualization.type) {
    case 'duval': {
      // Duval Triangle always plots points based on 3 gasses assigned to left, right and bottom (CH4, C2H4, C2H2 or CH4, C2H4, C2H6 or  H2, C2H4, C2H6)
      return (visualization.configuration.plots || [])
        .flatMap((plot) => [plot.leftId, plot.rightId, plot.bottomId])
        .map((signalId) => visualization.variables.find((v) => v.id === signalId?.split('.')[0]))
        .filter(Boolean);
    }
    case 'duvalPentagon': {
      // Duval Pentagon always plots points based on the same 5 gasses (H2, C2H6, CH4, C2H4, C2H2)
      return (visualization.configuration.plots || [])
        .flatMap((plot) => [plot.h2Id, plot.c2h6Id, plot.ch4Id, plot.c2h4Id, plot.c2h2Id])
        .map((signalId) => visualization.variables.find((v) => v.id === signalId?.split('.')[0]))
        .filter(Boolean);
    }
    case 'star': {
      // Star point always plots points based on 3 vectors assigned to L1, L2 and L3
      return getStarPlotVariables(visualization);
    }
    default: {
      return visualization.variables;
    }
  }
}

const SignalViewerContainer = React.memo(
  ({
    selectedVisualizations,
    pollingDateRange: panelPollingRange,
    onFormReset,
    theme,
    tz,
    site,
    canDeleteTemplate,
  }) => {
    const dispatch = useDispatch();
    const tfrTemplates = useSelector(fetchTfrTemplates);
    const [showNewTemplate, setShowNewTemplate] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [templateData, setTemplateData] = useState({});
    const [signalData, setSignalData] = useState({});
    const [currentRequestDetails, setCurrentRequestDetails] = useState({});
    const [initialActiveSignals, setInitialActiveSignals] = useState([]);

    const variables = useSelector(getSiteVariables);
    const sources = useSelector(getSources);

    const formattedInitialSignals = useMemo(
      () =>
        selectedVisualizations.map((visualization) => {
          // Include every configured signal, even non-plottable ones (e.g. unsupported dataType),
          // so they still show as selected and appear in the chart's legend
          const signalVariables = getVisualizationSignalVariables(visualization).filter(
            (variable) => variables.some((siteVariable) => siteVariable.id === variable.id)
          );

          return signalVariables
            .map((variable) => ({
              ...variable,
              visualization_name: visualization.name,
              // Use the first variable's aggregate as default for rest of them in a visualization
              aggregate: visualization.variables[0].aggregate,
            }))
            .slice(0, 10);
        }),
      [selectedVisualizations, variables]
    );

    const formattedSignalData = useMemo(() => {
      if (Object.keys(signalData).length > 0) {
        return Object.entries(signalData).flatMap(([signalId, aggs]) =>
          Object.keys(aggs).map((aggregate) => ({
            id: signalId,
            aggregate,
          }))
        );
      }
      return [];
    }, [JSON.stringify(signalData)]);

    const pollingDateRange = useDateSubscription(SIGNAL_VIEWER_DATETIME);

    // Show every variable in the signal list, including non-plottable ones, so a signal
    // already referenced by a selected visualization still appears there as selected.
    const variablesWithSourceType = useMemo(
      () =>
        variables.map((variable) => {
          const variableSource = sources.find((source) => source.id === variable.source_id);
          return {
            ...variable,
            sourceType: variableSource?.type || 'unknown',
          };
        }),
      [variables, sources]
    );

    const allowedSignalIds = useMemo(
      () => new Set(variables.filter(isPlottableSignal).map((variable) => variable.id)),
      [variables]
    );

    useBackgroundFreeze();

    useEffect(() => {
      if (panelPollingRange) {
        const relativeAdjustedRange = panelPollingRange?.label
          ? getRangeFromLabel(panelPollingRange.label)
          : getRangeFromPartial(panelPollingRange);

        dispatch(setPollingDateRange(relativeAdjustedRange, SIGNAL_VIEWER_DATETIME));
      }
    }, [panelPollingRange]);

    const mergeData = (previousData, newData) => {
      const merged = { ...previousData };
      for (const dataSet of newData) {
        merged[dataSet.variable] = previousData[dataSet.variable] || {};
        if (DEFAULT_AGGREGATION_OPTIONS.some((f) => f.value === dataSet.aggregate?.toLowerCase()))
          merged[dataSet.variable][dataSet.aggregate.toLowerCase()] = dataSet.values;
        else merged[dataSet.variable].avg = dataSet.values;
      }
      return merged;
    };

    useEffect(() => {
      const requestVariables = (
        formattedSignalData.length > 0 ? formattedSignalData : formattedInitialSignals.flat()
      ).filter((variable) => allowedSignalIds.has(variable.id));
      const request = {
        startDate: pollingDateRange.startDate,
        endDate: pollingDateRange.endDate,
        granularity: getBestFitGranularity(
          (pollingDateRange.endDate - pollingDateRange.startDate) / 1000,
          2500
        ),
        variables: requestVariables,
      };
      setCurrentRequestDetails(request);
      setInitialActiveSignals(formattedInitialSignals);
      if (requestVariables.length > 0) {
        setShowLoader(true);
        dispatch(
          fetchExtendedSeriesData(request, (data) => {
            if (data.hasError) {
              console.error('Error retrieving data');
              setShowLoader(false);
            } else {
              setSignalData((previousData) => mergeData(previousData, data));
              setShowLoader(false);
            }
          })
        );
      } else {
        setSignalData({});
      }
    }, [pollingDateRange]);

    const onTimelineChange = ([from, to], aggregation, activeVariables, callback) => {
      const request = {
        startDate: from,
        endDate: to,
        aggregation,
        granularity: getBestFitGranularity((to - from) / 1000, 2500),
        variables: activeVariables.filter((variable) => allowedSignalIds.has(variable.id)),
      };
      setCurrentRequestDetails(request);
      // Nothing plottable is active (e.g. only an unsupported-dataType signal); skip the
      // fetch so the loader isn't left spinning on a request that never resolves
      if (request.variables.length === 0) {
        callback();
        return;
      }
      setShowLoader(true);
      dispatch(
        fetchExtendedSeriesData(request, (data) => {
          if (!data || data.hasError) {
            console.error('Error retrieving data');
            setShowLoader(false);
          } else {
            setSignalData((previousData) => mergeData(previousData, data));
            setShowLoader(false);
            callback();
          }
        })
      );
    };

    const onCloseModal = useCallback(() => {
      setShowNewTemplate(false);
    });

    const onTemplateActions = useCallback((action) => {
      setTemplateData(action);
      if (action.actionType === 'save') {
        setShowLoader(true);
        const data = {
          site: site.id,
          name: action.name,
          value: { template: action.template },
          org: site.org,
          templateType: 'signal',
        };
        dispatch(
          updateTfrTemplate(action.id, data, () => {
            setShowLoader(false);
          })
        );
      } else {
        setShowNewTemplate(true);
      }
    }, []);

    const [eventDetail, onEventDetail] = useEventModal();

    const userEventTypes = useSelector(getUserEventTypes);
    const onOpenEvent = useCallback(
      (event) => {
        // Guards against an unresolved viewer event click (e.g. an aggregate count row)
        if (!event) {
          return;
        }

        onEventDetail({
          modalType: EVENT_TYPE_MACH,
          event,
          eventId: event.id,
          userEventTypes,
          site,
        });
      },
      [userEventTypes, site, onEventDetail]
    );

    const getEvents = useCallback(
      async ({ from, to }) => {
        const events = await services.data
          .getEventBuckets({
            site: site.id,
            from,
            to,
            interval: Math.round((to - from) / 400), // 400 event buckets
            aggregationType: 'time_severity_lists',
          })
          .then(
            (response) => response,
            (error) => {
              console.error('Unable to fetch iot events:', error);
              dispatch(checkIsOnline());
              dispatch(displayNotification(getNotification('getIotEvents', 'error')()));
              return { values: [] };
            }
          );
        return events.values;
      },
      [site]
    );

    const onDownloadSignalViewer = (activeSignals) => {
      const requestDetails = currentRequestDetails;
      delete requestDetails.variables;
      const seriesData = Object.values(activeSignals).flatMap((chartSignals) =>
        chartSignals
          .filter(([signalId]) => allowedSignalIds.has(signalId))
          .map(([signalId, aggregate]) => {
            const variableOfSeries = variables.find((v) => v.id === signalId);
            return {
              variableName: variableOfSeries.name,
              values: signalData[signalId][aggregate],
              aggregate,
              variable: signalId,
            };
          })
      );
      exportTimeSeriesToFile({
        ...requestDetails,
        siteName: site.name,
        seriesData,
        variables,
        exportType: 'xlsx',
        onExportCompleted: () => {},
      });
    };

    return (
      <>
        {selectedVisualizations.length <= 6 && (
          <div className="tfr-view">
            <div className="tfr-view-details">
              {showLoader && (
                <div className="page-loader">
                  <Loader
                    text={'Loading...'}
                    overlay
                  />
                </div>
              )}
              {((selectedVisualizations.length > 0 && initialActiveSignals.length > 0) ||
                selectedVisualizations.length === 0) && (
                <>
                  <SignalViewer
                    initialActiveSignals={initialActiveSignals}
                    signalData={signalData}
                    siteVariables={variablesWithSourceType}
                    initialChartCount={Math.max(selectedVisualizations.length, 3)}
                    pollingDateRange={pollingDateRange}
                    getEventsCallback={getEvents}
                    selectEventCallback={onOpenEvent}
                    onTimelineChange={onTimelineChange}
                    theme={theme}
                    onTemplateActions={onTemplateActions}
                    templateList={tfrTemplates}
                    onDownloadSignalViewer={onDownloadSignalViewer}
                    tz={tz}
                    DateTimePickerComponent={
                      <SelectPollingDateRange
                        enableGlobalDates={false}
                        panelId={SIGNAL_VIEWER_DATETIME}
                      />
                    }
                    aggregationOptions={
                      config?.onPremise
                        ? DEFAULT_ON_PREMISE_AGGREGATION_OPTIONS
                        : DEFAULT_AGGREGATION_OPTIONS
                    }
                    loading={showLoader}
                    canDeleteTemplate={canDeleteTemplate}
                  >
                    <Button
                      icon={<Icon icon="back" />}
                      onClick={onFormReset}
                      activity="secondary"
                      slim
                    >
                      Close
                    </Button>
                  </SignalViewer>
                  {eventDetail}
                </>
              )}
              {showNewTemplate && (
                <NewTemplateModal
                  templateData={templateData}
                  onCloseModal={onCloseModal}
                  tfrTemplates={tfrTemplates}
                  site={site}
                  setShowLoader={setShowLoader}
                />
              )}
            </div>
          </div>
        )}
        {selectedVisualizations.length > 6 && (
          <ConfirmationDialog
            modal={true}
            onCancel={onFormReset}
            onConfirm={onFormReset}
            title="Warning"
            body={
              <>
                <p>
                  Selected visualizations exceeds the supported count. Please select up to 6
                  visualizations.
                </p>
              </>
            }
            cancelText="Dismiss"
            confirmText="Close"
            confirmType="info"
          />
        )}
      </>
    );
  }
);

export default SignalViewerContainer;
